import type { KbAdminEditorSectionProps } from "../kbAdminEditorProps";

type Props = Pick<
  KbAdminEditorSectionProps,
  | "runAiHelp"
  | "aiBusy"
  | "aiQuestionAnswers"
  | "resetAiSession"
  | "appliedSuggestion"
  | "aiUpdateTitle"
  | "setAiUpdateTitle"
  | "aiUpdateMetadata"
  | "setAiUpdateMetadata"
  | "preserveOriginalBody"
  | "setPreserveOriginalBody"
  | "articleContent"
  | "aiSuggestedAction"
  | "aiTopicComplexity"
  | "aiRecommendedDocCount"
  | "aiStopReason"
  | "aiArticleLinkSuggestions"
  | "aiKbGapCandidates"
  | "aiLinksOpen"
  | "setAiLinksOpen"
  | "aiGapsOpen"
  | "setAiGapsOpen"
  | "gapSaving"
  | "saveGapsToInbox"
>;

export function KbAiDetailsRunAndGapBlocks(props: Props) {
  const {
    runAiHelp,
    aiBusy,
    aiQuestionAnswers,
    resetAiSession,
    appliedSuggestion,
    aiUpdateTitle,
    setAiUpdateTitle,
    aiUpdateMetadata,
    setAiUpdateMetadata,
    preserveOriginalBody,
    setPreserveOriginalBody,
    articleContent,
    aiSuggestedAction,
    aiTopicComplexity,
    aiRecommendedDocCount,
    aiStopReason,
    aiArticleLinkSuggestions,
    aiKbGapCandidates,
    aiLinksOpen,
    setAiLinksOpen,
    aiGapsOpen,
    setAiGapsOpen,
    gapSaving,
    saveGapsToInbox,
  } = props;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-amber-950 disabled:opacity-50 dark:bg-amber-600 dark:text-amber-50"
          onClick={() => void runAiHelp()}
          disabled={aiBusy}
        >
          {aiBusy ? "Processing…" : aiQuestionAnswers.some((item) => item.answer.trim()) ? "Run AI with answers" : "Run AI processing"}
        </button>
        <button
          type="button"
          className="rounded-lg border border-gray-900 bg-gray-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          onClick={() => void runAiHelp("stop_and_draft")}
          disabled={aiBusy}
        >
          {aiBusy ? "Generating…" : "Generate draft"}
        </button>
        <button
          type="button"
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
          onClick={() => resetAiSession({ keepPurpose: true })}
          disabled={aiBusy && !appliedSuggestion}
        >
          Clear AI session
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-700 dark:text-zinc-300">
          <input type="checkbox" checked={aiUpdateTitle} onChange={(event) => setAiUpdateTitle(event.target.checked)} />
          Update title
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-700 dark:text-zinc-300">
          <input type="checkbox" checked={aiUpdateMetadata} onChange={(event) => setAiUpdateMetadata(event.target.checked)} />
          Update permissions & metadata
        </label>
        {articleContent.trim() ? (
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-700 dark:text-zinc-300">
            <input type="checkbox" checked={preserveOriginalBody} onChange={(event) => setPreserveOriginalBody(event.target.checked)} />
            Preserve original body
          </label>
        ) : null}
        <span className="self-center text-xs text-gray-600 dark:text-zinc-400">
          AI updates editor fields and returns questions, but nothing is saved until you submit.
        </span>
      </div>

      {appliedSuggestion ? (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-3 text-xs text-indigo-950 space-y-1 shadow-sm dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-100">
          <div className="font-semibold text-indigo-950 dark:text-indigo-100">AI processing result</div>
          <div>title {appliedSuggestion.title || "(none)"}</div>
          <div>
            audience {appliedSuggestion.audience} • scope {appliedSuggestion.scope} • type {appliedSuggestion.doc_type || "(none)"} • status{" "}
            {appliedSuggestion.status}
          </div>
          <div>tags {(appliedSuggestion.tags || []).join(", ") || "(none)"}</div>
          {aiSuggestedAction ? <div>suggested next move {aiSuggestedAction.replaceAll("_", " ")}</div> : null}
          {aiTopicComplexity ? (
            <div>
              topic complexity {aiTopicComplexity} • recommended docs {aiRecommendedDocCount}
            </div>
          ) : null}
          {aiStopReason ? <div>stop reason {aiStopReason.replaceAll("_", " ")}</div> : null}
          {appliedSuggestion.accessSummary ? <div>{appliedSuggestion.accessSummary}</div> : null}
        </div>
      ) : null}

      {aiArticleLinkSuggestions.length || aiKbGapCandidates.length ? (
        <div className="rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-100 via-yellow-50 to-amber-100 px-3 py-3 shadow-sm shadow-amber-200/60 animate-pulse dark:border-amber-700 dark:from-amber-950/50 dark:via-amber-950/30 dark:to-amber-950/50 dark:shadow-amber-950/20">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-amber-950 dark:text-amber-100">AI linking and gap review</div>
              <div className="text-xs text-amber-900 dark:text-amber-200">
                {aiArticleLinkSuggestions.length} link suggestion{aiArticleLinkSuggestions.length === 1 ? "" : "s"} and {aiKbGapCandidates.length}{" "}
                possible gap
                {aiKbGapCandidates.length === 1 ? "" : "s"} identified from this drafting session.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {aiArticleLinkSuggestions.length ? (
                <button
                  type="button"
                  className="rounded-full border border-sky-300 bg-white/80 px-2 py-1 text-[11px] font-semibold text-sky-900 dark:border-sky-700 dark:bg-sky-950/50 dark:text-sky-100"
                  onClick={() => setAiLinksOpen((previous) => !previous)}
                >
                  {aiLinksOpen ? "hide links" : "review links"}
                </button>
              ) : null}
              {aiKbGapCandidates.length ? (
                <button
                  type="button"
                  className="rounded-full border border-amber-400 bg-white/80 px-2 py-1 text-[11px] font-semibold text-amber-900 dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-100"
                  onClick={() => setAiGapsOpen((previous) => !previous)}
                >
                  {aiGapsOpen ? "hide gaps" : "review gaps"}
                </button>
              ) : null}
              <button
                type="button"
                className="rounded-full border border-emerald-400 bg-white/80 px-2 py-1 text-[11px] font-semibold text-emerald-900 disabled:opacity-50 dark:border-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-100"
                onClick={() => void saveGapsToInbox()}
                disabled={gapSaving}
              >
                {gapSaving ? "Saving…" : "Save to inbox"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {aiKbGapCandidates.length && aiGapsOpen ? (
        <div className="space-y-2">
          <div className="text-xs font-medium text-amber-950 dark:text-amber-100">Predicted KB gaps</div>
          {aiKbGapCandidates.map((item) => (
            <div key={item.title} className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 space-y-2 dark:border-amber-800 dark:bg-amber-950/35">
              <div className="flex items-center justify-between gap-3">
                <div className="font-medium text-gray-900 dark:text-zinc-100">{item.title}</div>
                <span
                  className={
                    "rounded-full border px-2 py-0.5 text-[11px] " +
                    (item.priority === "high"
                      ? "border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100"
                      : item.priority === "low"
                        ? "border-gray-300 bg-gray-100 text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
                        : "border-amber-300 bg-white text-amber-900 dark:border-amber-700 dark:bg-zinc-900/60 dark:text-amber-100")
                  }
                >
                  {item.priority} priority
                </span>
              </div>
              <div>{item.summary}</div>
              {item.linked_articles.length ? (
                <div className="text-[11px] text-gray-500 dark:text-zinc-400">surrounding docs: {item.linked_articles.map((link) => link.slug).join(", ")}</div>
              ) : null}
              <textarea className="h-36 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-200" value={item.article_draft} readOnly />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}
